import _ from 'lodash';
import type { HomeScreenItemType } from '@/homescreen/HomeScreenTable.organism';
import type { PageSizeByTableItem, SortByTableItem } from '@/homescreen/homescreen.constants';
import type {
  HomeScreenFolder,
  HomeScreenTableKey,
  ItemsByTableEntry,
  PageNumberEntry,
} from '@/homescreen/homescreen.types';

/** Tables start on the first page until something records a different page for them. */
export const DEFAULT_PAGE_NUMBER = 1;
const DEFAULT_PAGE_SIZE = 15;

/** Current page for `table`, defaulting to the first page when the table has no entry yet. */
export const getPageNumberForTable = (pageNumberByTable: PageNumberEntry[], table?: HomeScreenTableKey): number =>
  pageNumberByTable.find((entry) => entry.key === table)?.pageNumber ?? DEFAULT_PAGE_NUMBER;

export const getSortForTable = (
  sortByTable: SortByTableItem[],
  table?: HomeScreenTableKey,
): SortByTableItem | undefined => sortByTable.find((entry) => entry.key === table);

export const getPageSizeByTable = (pageSizeByTable: PageSizeByTableItem[], table?: HomeScreenTableKey): number =>
  pageSizeByTable.find((entry) => entry.key === table)?.size ?? DEFAULT_PAGE_SIZE;

/** Items loaded for `table`, or `null` when the table has never been loaded. */
export const getItemsForTable = (
  itemsByTable: ItemsByTableEntry[],
  table?: HomeScreenTableKey,
): HomeScreenItemType[] | null => itemsByTable.find((entry) => entry.key === table)?.items ?? null;

export const getItemTotalForTable = (itemsByTable: ItemsByTableEntry[], table?: HomeScreenTableKey): number =>
  itemsByTable.find((entry) => entry.key === table)?.totalResults ?? 0;

export const isTableLoading = (loadingTable: HomeScreenTableKey[], table: HomeScreenTableKey): boolean =>
  loadingTable.includes(table);

/* --- Folder tree shaping ------------------------------------------------------------------ */

/**
 * Indexes every folder in `folders` (including subfolders, recursively) by its lower-cased ID so that
 * incoming folders can be matched against the tree regardless of ID casing.
 */
const folderIndex = (folders: HomeScreenFolder[]): Record<string, HomeScreenFolder> => {
  const index: Record<string, HomeScreenFolder> = {};
  const indexFolders = (foldersToIndex?: HomeScreenFolder[]) => {
    _.forEach(foldersToIndex ?? [], (folder) => {
      index[_.toLower(folder.id)] = folder;
      indexFolders(folder.subfolders);
    });
  };

  indexFolders(folders);

  return index;
};

/** Sorts a folder tree by name at every level, case-insensitively and with ties broken by exact name. */
export const sortFolderTree = (folders: HomeScreenFolder[]): HomeScreenFolder[] =>
  _.sortBy(folders, [(folder) => (folder.name ?? '').toLowerCase(), 'name']).map((folder) => ({
    ...folder,
    subfolders: folder.subfolders ? sortFolderTree(folder.subfolders) : folder.subfolders,
  }));

/**
 * Merges `incomingTree` into `tree`, updating folders already present by ID and appending the rest.
 * A folder's subfolders are only replaced when the incoming folder carries a `subfolders` key, so a
 * partial folder payload does not discard children that have already been loaded.
 */
export const mergeFolderTree = (tree: HomeScreenFolder[], incomingTree: HomeScreenFolder[]): HomeScreenFolder[] => {
  const merged = _.cloneDeep(tree);
  const index = folderIndex(merged);

  for (const folder of incomingTree) {
    const existing = index[_.toLower(folder.id)];
    if (existing) {
      _.assign(existing, _.omit(folder, ['subfolders']));
      if ('subfolders' in folder) {
        existing.subfolders = folder.subfolders ?? [];
      }
    } else {
      merged.push(folder);
    }
  }

  return sortFolderTree(merged);
};

/** Replaces the children of `folderId`, leaving the tree untouched when that folder is not loaded. */
export const replaceFolderChildren = (
  tree: HomeScreenFolder[],
  folderId: string,
  subfolders: HomeScreenFolder[],
): HomeScreenFolder[] => {
  const updated = _.cloneDeep(tree);
  const existing = folderIndex(updated)[_.toLower(folderId)];

  if (!existing) {
    return tree;
  }

  existing.subfolders = subfolders ?? [];

  return sortFolderTree(updated);
};
